import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  validateMFA,
  resendMFACode,
  selectMFAResendCodeTimeout,
  selectMFAResendCodeDataState,
  selectValidateMFADataState,
} from '../state/auth/auth.slice';
import { AuthNavigatorParamsList } from '../navigators/AuthNavigator/AuthNavigator.types';

import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { Screen, useNavigation } from '@betty/navigation';
import { Text, Box, Timer } from '@betty/moonlight';
import { OTCInput } from '../components';

export type MFAScreenProps = StackScreenProps<
  AuthNavigatorParamsList,
  'MFAScreen'
>;

function MFAScreen({ route }: MFAScreenProps) {
  const dispatch = useDispatch();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const validateDataState = useSelector(selectValidateMFADataState);
  const resendDataState = useSelector(selectMFAResendCodeDataState);
  const navigation =
    useNavigation<StackNavigationProp<AuthNavigatorParamsList>>();
  const resendTimeout = useSelector(selectMFAResendCodeTimeout);
  const { email, token, codeChallenge, codeVerifier } = route.params || {};

  const onFulfill = useCallback(async () => {
    const result = await dispatch(
      validateMFA({ email, token, codeChallenge, codeVerifier, code }),
    );

    if (validateMFA.rejected.match(result)) {
      setCode('');

      const { errorMessages } = result.payload?.data || {};

      if (errorMessages?.[0]) {
        setError(errorMessages[0]);
      }

      // TODO: Localize error messages
      // if (errorCode) {
      //   setError(errorCode);
      // }
    }
  }, [email, token, codeChallenge, codeVerifier, code, dispatch]);

  const onChange = useCallback((e: string) => {
    setCode(e);
    setError('');
  }, []);

  const onResendCodePress = useCallback(async () => {
    if (resendDataState !== 'idle') {
      return;
    }

    const result = await dispatch(resendMFACode({ email, token }));

    if (resendMFACode.fulfilled.match(result)) {
      const { token: newToken } = result.payload;

      navigation.setParams({ token: newToken });
    }
  }, [email, token, dispatch, navigation, resendDataState]);

  return (
    <Screen>
      <Box flex={1} justifyContent="space-between">
        <Box gap="space-600">
          <Text
            variant="h4"
            gradient="darkSurfaceAccent"
            locNS="auth"
            locKey="mfaTitle"
          />
          <Text
            variant="body-m"
            color="darkSurfacePrimary"
            locNS="auth"
            locKey="mfaDescription"
          />
          <OTCInput
            codeLength={6}
            value={code}
            loading={validateDataState === 'pending'}
            onChange={onChange}
            onFulfill={onFulfill}
          />
          {error ? (
            <Text color="darkSurfaceError" variant="body-m">
              {error}
            </Text>
          ) : null}
          <Box justifyContent="center" alignItems="center">
            {resendTimeout ? (
              <Box justifyContent="center" flexDirection="row" gap="space-050">
                <Text
                  variant="body-s"
                  color="darkSurfacePrimary"
                  locNS="auth"
                  locKey="resendCodeIn"
                />
                <Timer
                  color="darkSurfacePrimary"
                  variant="body-s-semi"
                  time={resendTimeout / 1000}
                />
              </Box>
            ) : (
              <Text
                variant="link-small"
                color="darkSurfacePrimary"
                locNS="auth"
                locKey="resendCode"
                onPress={onResendCodePress}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Screen>
  );
}

export default MFAScreen;
